import { IResourceRecord } from '~/client/src/desktop/components/Resource/types'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceFieldProjector } from '~/client/src/shared/resources/projection/types'

import { expandAllWhenBookable } from './expandAllWhenBookable'
import {
  PERMITTED_COMPANIES_KEY,
  SHOW_IN_MAP_KEY,
  WORKFLOW_RESOURCE_KEY,
} from './resourceControlsColumns'

export interface IControlsGroupEntry {
  id: string
  label: string
}

const booleanGroupEntry = (value: boolean): IControlsGroupEntry =>
  value
    ? { id: 'true', label: Localization.translator.yes }
    : { id: 'false', label: Localization.translator.no }

export interface IResolveGroupDeps {
  fieldProjector: IResourceFieldProjector
  getCompanyName: (id: string) => string | undefined
  allCompanyIds: string[]
}

/**
 * The group entries a record belongs to for a given grouping key.
 *
 * The control field (Permitted Companies) reads off
 * `record.root`: a record with values groups under each value; an empty value on
 * a **bookable** resource means "All" (every option), so it expands to every
 * option id — the record appears under every group, mirroring how the filter
 * treats "All" (R9). A non-bookable empty value yields no entries (→ Unassigned).
 */
export function resolveControlsGroupEntries(
  record: IResourceRecord,
  grouping: string,
  deps: IResolveGroupDeps,
): IControlsGroupEntry[] {
  const isBookable = record.root?.isBookable ?? false

  const expandAll = (
    values: string[],
    allIds: string[],
    getLabel: (id: string) => string | undefined,
  ): IControlsGroupEntry[] =>
    expandAllWhenBookable(values, isBookable, allIds).map(id => ({
      id,
      label: getLabel(id) ?? id,
    }))

  if (grouping === PERMITTED_COMPANIES_KEY) {
    return expandAll(
      record.root?.permittedCompanies ?? [],
      deps.allCompanyIds,
      deps.getCompanyName,
    )
  }
  // WR (isBookable) and Show in Maps group into two buckets — on (Yes) / off
  // (No) — using the same boolean labels the checkbox data fields display. Show
  // in Maps is independent of the WR gate and defaults to shown when unset.
  if (grouping === WORKFLOW_RESOURCE_KEY) {
    return [booleanGroupEntry(isBookable)]
  }
  if (grouping === SHOW_IN_MAP_KEY) {
    return [booleanGroupEntry(record.root?.showInMap ?? true)]
  }
  return []
}
